<template>
  <ion-page>
    <ion-header>
      <ion-progress-bar v-if="loading" type="indeterminate" color="light" />
      <ion-toolbar>
        <ion-title>Add to existing song</ion-title>
        <ion-buttons slot="primary">
          <ion-button @click="close" :disabled="loading" color="light">
            Close
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content>
      <ais-instant-search
        ref="instantSearch"
        :search-client="searchClient"
        :index-name="indexName"
      >
        <ais-configure :filters="'entityType:SONG'" />

        <ion-toolbar class="searchbox-toolbar">
          <ais-search-box>
            <template v-slot="{ refine }">
              <ion-searchbar
                @ion-clear="clearSearch($event, refine)"
                v-model="searchValue"
                @input="
                  refine($event.currentTarget.value);
                  searchChange($event, refine);
                "
              />
            </template>
          </ais-search-box>
        </ion-toolbar>

        <ais-infinite-hits>
          <template v-slot="{ items, refineNext, isLastPage }">
            <ion-list style="max-width: 940px; background: transparent">
              <ion-item
                @click="handleSongSelected(item.songId)"
                v-for="item of items"
                :key="item.songId"
              >
                <span slot="start">
                  <slot name="imagePlaceholder">
                    <img
                      class="card-image-placeholder"
                      src="assets/song-placeholder.png"
                    />
                  </slot>
                </span>

                <ion-label>
                  <h2 style="font-weight: bold">{{ item.title }}</h2>
                  <p class="subtitle">{{ item.artist }}</p>
                </ion-label>
              </ion-item>
            </ion-list>

            <div
              style="text-align: center; width: 100%; margin-bottom: 30px"
              v-if="items.length === 0 && searchValue.length > 0"
            >
              No results found 🎵⚡️
            </div>

            <ion-infinite-scroll
              :disabled="isLastPage"
              @ionInfinite="loadMore($event, refineNext, isLastPage)"
              threshold="100px"
            >
              <ion-infinite-scroll-content loading-spinner="dots" />
            </ion-infinite-scroll>

            <ais-refresh-search ref="searchRefresh" />
          </template>
        </ais-infinite-hits>
      </ais-instant-search>
    </ion-content>
  </ion-page>
</template>

<script>
import { defineComponent } from 'vue';
import {
  IonContent,
  modalController,
  loadingController,
  IonProgressBar,
  IonTitle,
  IonButton,
  IonButtons,
  IonToolbar,
  IonHeader,
  IonSearchbar,
  IonList,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonItem,
  IonLabel,
  IonPage,
  toastController,
} from '@ionic/vue';
import algoliasearch from 'algoliasearch/lite';
import {
  AisInstantSearch,
  AisSearchBox,
  AisConfigure,
} from 'vue-instantsearch/vue3/es';
import AisRefreshSearch from '@/components/search/AisRefreshSearch.vue';

export default defineComponent({
  name: 'AddToExistingSong',

  components: {
    IonContent,
    IonProgressBar,
    IonTitle,
    IonButton,
    IonButtons,
    IonToolbar,
    IonHeader,
    IonSearchbar,
    IonList,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    IonItem,
    IonLabel,
    AisInstantSearch,
    AisSearchBox,
    AisRefreshSearch,
    AisConfigure,
    IonPage,
  },

  props: {
    fileBlob: { type: Blob },
    filename: { type: String },
  },

  data() {
    return {
      searchValue: '',
      searchClient: algoliasearch(
        process.env.VUE_APP_ALGOLIA_APP_ID,
        this.$store.state.user.searchKey,
      ),
      loading: false,
    };
  },

  computed: {
    indexName: () => process.env.VUE_APP_CRATE_CONTENT_INDEX_NAME,
  },

  methods: {
    clearSearch($event, refine) {
      refine('');
      this.searchValue = '';
    },

    searchChange($event) {
      this.searchValue = $event.currentTarget.value;
    },

    loadMore(e, refineNext) {
      refineNext();
      e.target.complete();
    },

    async handleSongSelected(songId) {
      const loading = await loadingController.create({
        message: 'Adding version to song...',
      });
      await loading.present();

      try {
        await this.$store.dispatch('startFileUpload', {
          selectedFile: this.file,
          filename: this.filename,
          songId: songId,
          active: true,
        });
        await this.showSuccessMessage();
        await loading.dismiss();
        modalController.dismiss();
      } catch (error) {
        await this.showErrorMessage(error);
        await loading.dismiss();
      }
    },

    async showSuccessMessage() {
      const toast = await toastController.create({
        header: '',
        message: 'Added to song',
        buttons: [
          {
            text: 'Ok',
            role: 'cancel',
            handler: () => {
              console.log('Cancel clicked');
            },
          },
        ],
      });
      await toast.present();
    },

    async showErrorMessage(err) {
      const message = err.message.includes('limit')
        ? "You're out of space! Visit account settings for more info."
        : 'Sorry! We had trouble creating the song. Please try again later.';

      const toast = await toastController.create({
        header: 'Uh oh!',
        message,
        buttons: [
          {
            text: 'Ok',
            role: 'cancel',
            handler: () => {
              console.log('Cancel clicked');
            },
          },
        ],
      });
      await toast.present();
    },

    async close() {
      modalController.dismiss();
    },
  },
});
</script>

<style scoped>
ion-content {
  --background: #111111;
}

ion-toolbar {
  --background: #111111;
  --border-style: none;
}

.searchbox-toolbar {
  --border-style: none;
  margin: 0 0 8px 0;
}

.styled-search {
  height: 64px;
}

.styled-search .searchbar-input-container.sc-ion-searchbar-ios {
  height: 48px;
}

.styled-search input.searchbar-input {
  height: 48px;
  padding: 0 45px !important;
}

.styled-search .searchbar-search-icon {
  left: 17px;
}

.card-image-placeholder {
  height: 56px;
  width: 56px;
  border-radius: 5px;
  background-color: #606060;
}

ion-item {
  --background: transparent;
  --border-style: none;
}

ion-searchbar {
  --background: #333333;
  --border-radius: 50px;
  --icon-color: white;
  --clear-button-color: white;
  --placeholder-color: white;
  --color: white;
  padding-bottom: 0;
}
</style>
